import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { SnackBarStoreAction } from "Store/SnackBar";
import {
  getBulkAttendance,
  updateAttendance,
  getAttendanceNa,
} from "../../../services/userServices";
import { useTranslation } from "react-i18next";
import BackdropComponent from "../../Spinner/Spinner";
import classes from "../Attendance/AttendanceV2.module.css";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import LastUpdateTooltip from "../Helpers/LastUpdateTooltip";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SnackBar from "../../SnackBar/SnackBar";
import { bulkEditStoreAction } from "../../../Store/BulkEdit";
import PropTypes from "prop-types";
import PowerSharpIcon from "@mui/icons-material/PowerSharp";
import applicationUsageLog from "../../../utils/applicationUsagesLog";

const getFilteredData = (data, property, value) =>
  data.filter((itm) => itm[property] === value);

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  padding: "8px 20px",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  padding: "8px 20px",
  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

const AttendanceTableCell = ({
  title,
  naValues,
  state,
  count,
  name,
  onChange,
}) => (
  <TableCell
    className={`${classes.tableHeaderCell} text-center align-center`}
    align="center"
    sx={{
      backgroundColor: "#e5e4e2 !important",
    }}
  >
    <Box
      sx={{
        pt: 0.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography
        sx={{
          fontFamily: "Urbanist",
          fontSize: "14px",
          fontWeight: "bold",
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      {naValues && (
        <Typography
          className={classes.redColor}
          sx={{ fontSize: "12px", mb: 0.5, color: "#3299FF" }}
        >
          N/A
        </Typography>
      )}
      <Box>
        <Checkbox
          type="checkbox"
          sx={{
            "& .MuiSvgIcon-root": {
              fontSize: 25,
            },
            color: "#FFFFFF",
            "&.Mui-checked": {
              color: "#3299FF",
            },
          }}
          checked={state}
          onChange={(event) => onChange(event)}
          name={name}
        />
      </Box>
      <Typography
        sx={{
          fontFamily: "Urbanist",
          fontSize: "14px",
          fontWeight: "bold",
          color: "#3299FF",
          mt: 0.5,
        }}
      >
        {count}
      </Typography>
    </Box>
  </TableCell>
);

AttendanceTableCell.propTypes = {
  title: PropTypes.string,
  naValues: PropTypes.any,
  state: PropTypes.bool,
  count: PropTypes.number,
  name: PropTypes.string,
  onChange: PropTypes.func,
};

const AttendanceV2 = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(["attendance"]);
  const [loading, setLoading] = useState(false);
  const classTraineeId = useSelector((state) => state.bulkEdit);
  const { classRequestId, classRosterId } = classTraineeId;
  const [attendanceData, setAttendanceData] = useState([]);

  const [filterData, setFilterData] = useState([]);
  const initialRender = useRef(true);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const { filterStatus } = props;
  const [order, setOrder] = useState("ASC");
  const [allData, setAllData] = useState([]);
  const [totalActiveCount, setTotalActiveCount] = useState({
    total: 0,
    active: 0,
  });

  const [tci, setTci] = useState({
    state: false,
    count: 0,
  });
  const [tciRecovery, setTciRecovery] = useState({
    state: false,
    count: 0,
  });
  const [ttd, setTtd] = useState({
    state: false,
    count: 0,
  });
  const [ttdRecovery, setTtdRecovery] = useState({
    state: false,
    count: 0,
  });
  const [ttf, setTtf] = useState({
    state: false,
    count: 0,
  });
  const [productTraining, setProductTraining] = useState({
    state: false,
    count: 0,
  });
  const [nesting, setNesting] = useState({
    state: false,
    count: 0,
  });
  const [production, setProduction] = useState({
    state: false,
    count: 0,
  });

  const [naValues, setNaValues] = useState({});

  const sorting = (col) => {
    if (order === "ASC") {
      const sorted = [...attendanceData].sort((a, b) =>
        a[col].toLowerCase() > b[col].toLowerCase() ? 1 : -1,
      );
      setAttendanceData(sorted);
      setOrder("DSC");
    }
    if (order === "DSC") {
      const sorted = [...attendanceData].sort((a, b) =>
        a[col].toLowerCase() < b[col].toLowerCase() ? 1 : -1,
      );
      setAttendanceData(sorted);
      setOrder("ASC");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    let obj = {
      classTraineeIdList: classTraineeId.traineeDetails,
    };
    const response = await getBulkAttendance(obj);
    if (response.message === "Success") {
      setFilterData(response.data);
      setAllData(response.data);
      setLoading(false);
      dispatch(bulkEditStoreAction.setAttendanceData(response.data));
      dispatch(bulkEditStoreAction.setAttendanceAllData(response.data));
    } else {
      setSnack({
        open: true,
        message: "Failed to load data!",
        severity: "error",
      });
      setLoading(false);
    }
    if (props.resetStatus === true) {
      setFilterData(response.data);
      props.onChange();

      setTci({
        state: false,
        count: 0,
      });
      setTciRecovery({
        state: false,
        count: 0,
      });
      setTtd({
        state: false,
        count: 0,
      });
      setTtdRecovery({
        state: false,
        count: 0,
      });
      setTtf({
        state: false,
        count: 0,
      });
      setProductTraining({
        state: false,
        count: 0,
      });
      setNesting({
        state: false,
        count: 0,
      });
      setProduction({
        state: false,
        count: 0,
      });
    }
    if (response.data) {
      filtering(props.filterStatus, response.data);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        await fetchData();

        if (cancelled) return;

        const response = await getAttendanceNa(props.roster);

        if (cancelled) return;

        if (response.status === true) {
          if (!cancelled) {
            setNaValues(response.data[0]);
          }
        }

        if (!cancelled) {
          applicationUsageLog(
            localStorage.getItem("User"),
            localStorage.getItem("EmailId"),
            "ASTT",
            "Attendance",
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to initialize attendance:", error);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const refreshData = async () => {
      if (props.resetStatus === true) {
        try {
          await fetchData();
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to refresh data:", error);
          }
        }
      }
    };

    refreshData();

    return () => {
      cancelled = true;
    };
  }, [props.resetStatus]);

  const filtering = (filter, data) => {
    let filterData = [];

    if (filter === "ALL") {
      filterData = data;
    } else {
      filterData = data.filter((item) => item.TraineeStatus === filter);
    }

    setAttendanceData(filterData);
    initialCheck(filterData);
    props.closeLoader();
  };

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      setLoading(true);

      const timeout = setTimeout(() => {
        filtering(props.filterStatus, filterData);
        setLoading(false);
      }, 1000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [props.filterStatus]);

  const initialCheck = (data) => {
    let activeCount = getFilteredData(data, "TraineeStatus", "Active");
    setTotalActiveCount({
      total: data.length,
      active: activeCount.length,
    });

    const attendedCounts = {
      AttendedTCIRecovery: "tciRecoveryCount",
      AttendedTPCLD: "tpcLdCount",
      AttendedTTF: "ttfCount",
      AttendedTechTest: "ttdBotCount",
      AttendedTechTestRecovery: "ttdRecoveryCount",
      AttendedTPCOG: "tpcOgCount",
      AttendedTPCTA: "tpcTACount",
      AttendedNesting: "nestigCount",
      AttendedProductTraining: "productTrainingCount",
      AttendedProduction: "productionCount",
      AttendedTCI: "tciCOunt",
    };
    const counts = Object.keys(attendedCounts).reduce((acc, key) => {
      acc[attendedCounts[key]] = data.filter((item) => item[key]).length;
      return acc;
    }, {});

    setNesting({ count: counts.nestigCount, state: false });
    setProductTraining({ state: false, count: counts.productTrainingCount });
    setProduction({ count: counts.productionCount, state: false });
    setTci({ count: counts.tciCOunt, state: false });
    setTciRecovery({ state: false, count: counts.tciRecoveryCount });
    setTtf({ state: false, count: counts.ttfCount });
    setTtd({ state: false, count: counts.ttdBotCount });
    setTtdRecovery({ count: counts.ttdRecoveryCount, state: false });
  };

  const setCountValues = (event, count, data) => {
    if (event === "AttendedNesting") {
      setNesting((prev) => ({
        ...prev,
        count: count,
      }));
    }
    if (event === "AttendedProductTraining") {
      setProductTraining((prev) => ({
        ...prev,
        count: count,
      }));
    }
    if (event === "AttendedProduction") {
      setProduction((prev) => ({
        ...prev,
        count: count,
      }));
    }
    if (event === "AttendedTCI") {
      setTci((prev) => ({
        ...prev,
        count: count,
      }));
    }
    if (event === "AttendedTCIRecovery") {
      setTciRecovery((prev) => ({
        ...prev,
        count: count,
      }));
    }
    if (event === "AttendedTTF") {
      setTtf((prev) => ({
        ...prev,
        count: count,
      }));
    }
    if (event === "AttendedTechTest") {
      setTtd((prev) => ({
        ...prev,
        count: count,
      }));
    }
    if (event === "AttendedTechTestRecovery") {
      setTtdRecovery((prev) => ({
        ...prev,
        count: count,
      }));
    }
  };

  const handleChangeChecked = (event, row, i) => {
    let trainee = attendanceData.map((item) => {
      if (
        item.OracleId === row.OracleId &&
        item.TraineeStatus === row.TraineeStatus
      ) {
        return {
          ...item,
          [event.target.name]: event.target.checked,
        };
      } else {
        return item;
      }
    });
    setAttendanceData(trainee);
    dispatch(bulkEditStoreAction.setAttendanceData(trainee));

    let data = filterData.map((item) => {
      if (
        item.OracleId === row.OracleId &&
        item.TraineeStatus === row.TraineeStatus
      ) {
        return {
          ...item,
          [event.target.name]: event.target.checked,
        };
      } else {
        return item;
      }
    });

    setFilterData(data);

    let count = 0;
    for (const value of trainee) {
      if (value[event.target.name]) {
        count = count + 1;
      }
    }

    setCountValues(event.target.name, count, trainee);
  };

  const validateAttendanceProperty = (item) => (item ? "true" : "false");

  const navigateBack = () => {
    navigate(
      `/home/searchrosters?classRequestId=${classRequestId}&classRosterId=${classRosterId}`,
    );
  };

  const createData = () =>
    attendanceData.map((itm) => ({
      classTraineeId: itm.ClassTraineeId,
      agentComments: itm.AgentComments,
      attendedNesting: validateAttendanceProperty(itm.AttendedNesting),
      attendedProduction: validateAttendanceProperty(itm.AttendedProduction),
      attendedProductTraining: validateAttendanceProperty(
        itm.AttendedProductTraining,
      ),
      attendedTCI: validateAttendanceProperty(itm.AttendedTCI),
      attendedTCIRecovery: validateAttendanceProperty(itm.AttendedTCIRecovery),
      attendedTechTest: validateAttendanceProperty(itm.AttendedTechTest),
      attendedTechTestRecovery: validateAttendanceProperty(
        itm.AttendedTechTestRecovery,
      ),
      attendedTTF: validateAttendanceProperty(itm.AttendedTTF),
      modifiedBy: localStorage.getItem("User"),
    }));

  const saveCloseHandler = async () => {
    setLoading(true);
    let data = createData();
    let update = await updateAttendance(data);
    if (update.message === "Success") {
      const obj = {
        open: true,
        message: "Update completed successfully.",
        severity: "success",
      };
      dispatch(bulkEditStoreAction.setPreviousTabName(""));
      dispatch(SnackBarStoreAction.updateSnack(obj));
      navigateBack();
    } else {
      setSnack({
        open: true,
        message: "Failed to apply changes.",
        severity: "error",
      });
    }

    setLoading(false);
  };

  const saveAttendance = async () => {
    setLoading(true);
    let data = createData();
    let update = await updateAttendance(data);
    if (update.message === "Success") {
      setSnack({
        open: true,
        message: "Update completed successfully.",
        severity: "success",
      });
      setAllData(filterData);
      fetchData();
    } else {
      setSnack({
        open: true,
        message: "Failed to apply changes.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleTraineeStatusCheck = (filterStatus, key, item, initial) =>
    item.TraineeStatus === filterStatus ? initial[key] : item[key];

  const findInitialData = (oracleId) =>
    allData.find((data) => data.OracleId === oracleId);

  const handleIndividualText = (event, row, key) => {
    let data = filterData.map((item) => {
      if (
        item.OracleId === row.OracleId &&
        item.TraineeStatus === row.TraineeStatus
      ) {
        return {
          ...item,
          [key]: event.target.value,
        };
      } else {
        return item;
      }
    });
    let trainee = attendanceData.map((item) => {
      if (
        item.OracleId === row.OracleId &&
        item.TraineeStatus === row.TraineeStatus
      ) {
        return {
          ...item,
          [key]: event.target.value === "" ? null : event.target.value,
        };
      } else {
        return item;
      }
    });
    setAttendanceData(trainee);
    setFilterData(data);
    dispatch(bulkEditStoreAction.setAttendanceData(trainee));
  };

  const updateAttendanceData = (key, value) => {
    if (value) {
      const updatedData = attendanceData.map((item) => ({
        ...item,
        [key]: value,
      }));
      setAttendanceData(updatedData);
      dispatch(bulkEditStoreAction.setAttendanceData(updatedData));
    } else {
      let oldAttendenceData = attendanceData.map((item) => {
        let initialData = findInitialData(item.OracleId);
        return {
          ...item,
          [key]: initialData[key],
        };
      });
      setAttendanceData(oldAttendenceData);
      dispatch(bulkEditStoreAction.setAttendanceData(oldAttendenceData));
    }
  };

  const updateFilterData = (key, value) => {
    const updatedData = filterData.map((item) => {
      if (
        props.filterStatus === "ALL" ||
        props.filterStatus === item.TraineeStatus
      ) {
        return {
          ...item,
          [key]: value,
        };
      } else {
        return {
          ...item,
        };
      }
    });
    setFilterData(updatedData);
  };

  const handleCheckChangeAll = (event) => {
    let updatedData;

    if (event.target.checked) {
      // Select all - set all to true
      updatedData = attendanceData.map((item) => ({
        ...item,
        [event.target.name]: true,
      }));
      updateFilterData(event.target.name, true);
    } else {
      // Deselect all - set all to false
      updatedData = attendanceData.map((item) => ({
        ...item,
        [event.target.name]: false,
      }));
      updateFilterData(event.target.name, false);
    }

    setAttendanceData(updatedData);
    dispatch(bulkEditStoreAction.setAttendanceData(updatedData));

    // Always count items that are TRUE (checked)
    const count = updatedData.filter(
      (item) => item[event.target.name] === true,
    ).length;

    // Update the respective state with both count and state
    const key = event.target.name;
    const checked = event.target.checked;

    switch (key) {
      case "AttendedNesting":
        setNesting({ state: checked, count });
        break;
      case "AttendedProductTraining":
        setProductTraining({ state: checked, count });
        break;
      case "AttendedProduction":
        setProduction({ state: checked, count });
        break;
      case "AttendedTCI":
        setTci({ state: checked, count });
        break;
      case "AttendedTCIRecovery":
        setTciRecovery({ state: checked, count });
        break;
      case "AttendedTTF":
        setTtf({ state: checked, count });
        break;
      case "AttendedTechTest":
        setTtd({ state: checked, count });
        break;
      case "AttendedTechTestRecovery":
        setTtdRecovery({ state: checked, count });
        break;
      default:
        break;
    }
  };

  const renderTextCell = (nameWithId, traineeStatus, isInactive) => (
    <Grid item xs={0.8} align="center">
      <Typography
        sx={{
          fontFamily: "Urbanist",
          fontSize: "13px",
          color: "#3F5765",
          textDecoration: isInactive ? "line-through" : "none",
        }}
      >
        {nameWithId}
      </Typography>
      {isInactive && <p className={classes.blueColor}>{traineeStatus}</p>}
    </Grid>
  );

  return (
    <div>
      {loading && <BackdropComponent />}
      {attendanceData.length > 0 && (
        <>
          <div>
            <TableContainer
              className={classes.container}
              component={Paper}
              sx={{ overflowX: "auto", overflowY: "auto" }}
            >
              <Table
                stickyHeader
                aria-label="sticky table"
                sx={{ minWidth: 2000 }}
              >
                <TableHead>
                  <TableRow>
                    {/* 1. FROZEN - Name/Oracle Id */}
                    <TableCell
                      className={`${classes.tableHeaderCell} text-center align-center`}
                      align="center"
                      sx={{
                        minWidth: "320px !important",
                        maxWidth: 320,
                        position: "sticky",
                        left: 0,
                        top: "0 !important",
                        zIndex: "1100 !important",
                        boxShadow: "2px 0 5px -2px rgba(0,0,0,0.15)",
                        cursor: "pointer",
                        backgroundColor: "#e5e4e2 !important",
                      }}
                      onClick={() => sorting("Name")}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Urbanist",
                          fontSize: "14px",
                          fontWeight: "bold",
                          mb: 0,
                        }}
                      >
                        {t("name")}{" "}
                        <ArrowUpwardIcon
                          sx={{
                            fontSize: 16,
                            color: order === "ASC" ? "#1976d2" : "#9e9e9e",
                            verticalAlign: "middle",
                          }}
                        />
                        <ArrowDownwardIcon
                          sx={{
                            fontSize: 16,
                            color: order === "DSC" ? "#1976d2" : "#9e9e9e",
                            verticalAlign: "middle",
                          }}
                        />
                      </Typography>
                      <Typography
                        className={classes.blueColor}
                        sx={{ fontSize: "14px", margin: "0" }}
                      >
                        Total: {totalActiveCount.total} | Active:{" "}
                        {totalActiveCount.active}
                      </Typography>
                    </TableCell>

                    {/* Attendance Table Cells */}
                    <AttendanceTableCell
                      title={t("TCI")}
                      naValues={naValues.TCINA}
                      state={tci.state}
                      count={tci.count}
                      name="AttendedTCI"
                      onChange={(event) => handleCheckChangeAll(event)}
                    />
                    <AttendanceTableCell
                      title={t("TCIRecovery")}
                      naValues={naValues.TCIRecoveryNA}
                      state={tciRecovery.state}
                      count={tciRecovery.count}
                      name="AttendedTCIRecovery"
                      onChange={(event) => handleCheckChangeAll(event)}
                    />
                    <AttendanceTableCell
                      title={t("TTD/BOT")}
                      naValues={naValues.TechTestNA}
                      state={ttd.state}
                      count={ttd.count}
                      name="AttendedTechTest"
                      onChange={(event) => handleCheckChangeAll(event)}
                    />
                    <AttendanceTableCell
                      title={t("TTDRecovery")}
                      naValues={naValues.TechTestRecoveryNA}
                      state={ttdRecovery.state}
                      count={ttdRecovery.count}
                      name="AttendedTechTestRecovery"
                      onChange={(event) => handleCheckChangeAll(event)}
                    />
                    <AttendanceTableCell
                      title={t("TTF")}
                      naValues={naValues.TTFNA}
                      state={ttf.state}
                      count={ttf.count}
                      name="AttendedTTF"
                      onChange={(event) => handleCheckChangeAll(event)}
                    />
                    <AttendanceTableCell
                      title={t("ProductTraining")}
                      state={productTraining.state}
                      count={productTraining.count}
                      name="AttendedProductTraining"
                      onChange={(event) => handleCheckChangeAll(event)}
                    />
                    <AttendanceTableCell
                      title={t("Nesting")}
                      naValues={naValues.NestingNA}
                      state={nesting.state}
                      count={nesting.count}
                      name="AttendedNesting"
                      onChange={(event) => handleCheckChangeAll(event)}
                    />
                    <AttendanceTableCell
                      title={t("Production")}
                      state={production.state}
                      count={production.count}
                      name="AttendedProduction"
                      onChange={(event) => handleCheckChangeAll(event)}
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendanceData.map((row, i) => {
                    const rowKey = `${row.OracleId}-${i}`;
                    return (
                      <TableRow key={rowKey} hover>
                        {/* 1. FROZEN - Employee + Comments */}
                        <TableCell
                          align="center"
                          sx={{
                            position: "sticky",
                            left: 0,
                            backgroundColor: "#fff",
                            zIndex: 1000,
                            boxShadow: "2px 0 5px -2px rgba(0,0,0,0.15)",
                            padding: "12px 8px",
                          }}
                        >
                          <Grid
                            container
                            direction="row"
                            justifyContent="center"
                            columns={1}
                            alignItems="center"
                            spacing={0}
                          >
                            {renderTextCell(
                              `${row.Name}(${row.OracleId})`,
                              row.TraineeStatus,
                              row.TraineeStatus !== "Active",
                            )}
                            {row.TTECRegistrationStatus ? (
                              <Grid item xs={0.1}>
                                <Tooltip title="TTEConnect: Registered">
                                  <PowerSharpIcon
                                    sx={{ color: "#69DE00", fontSize: 18 }}
                                  />
                                </Tooltip>
                              </Grid>
                            ) : (
                              <Grid item xs={0.1}></Grid>
                            )}
                          </Grid>
                          <Box sx={{ mt: 2, width: "100%" }}>
                            <LastUpdateTooltip
                              AgentCommentsModifiedDate={
                                row.AgentCommentsModifiedDate
                              }
                              AgentCommentsModifiedBy={
                                row.AgentCommentsModifiedBy
                              }
                            >
                              <textarea
                                className={classes.inputContainer}
                                value={
                                  row.AgentComments ? row.AgentComments : ""
                                }
                                maxLength={4000}
                                placeholder="Max 4000 characters"
                                onChange={(e) =>
                                  handleIndividualText(e, row, "AgentComments")
                                }
                              />
                            </LastUpdateTooltip>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                mt: 0.5,
                                px: 0.5,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: "Urbanist",
                                  fontSize: "10px",
                                  color: "#666",
                                }}
                              >
                                {(row.AgentComments || "").length}/4000
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Attendance Checkboxes */}
                        <TableCell align="center" sx={{ padding: "8px" }}>
                          <Checkbox
                            type="checkbox"
                            sx={{
                              "& .MuiSvgIcon-root": {
                                fontSize: 25,
                                color: "#3299FF",
                              },
                            }}
                            checked={row.AttendedTCI}
                            name="AttendedTCI"
                            onChange={(event) =>
                              handleChangeChecked(event, row, i)
                            }
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ padding: "8px" }}>
                          <Checkbox
                            type="checkbox"
                            sx={{
                              "& .MuiSvgIcon-root": {
                                fontSize: 25,
                                color: "#3299FF",
                              },
                            }}
                            checked={row.AttendedTCIRecovery}
                            name="AttendedTCIRecovery"
                            onChange={(event) =>
                              handleChangeChecked(event, row, i)
                            }
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ padding: "8px" }}>
                          <Checkbox
                            type="checkbox"
                            sx={{
                              "& .MuiSvgIcon-root": {
                                fontSize: 25,
                                color: "#3299FF",
                              },
                            }}
                            checked={row.AttendedTechTest}
                            name="AttendedTechTest"
                            onChange={(event) =>
                              handleChangeChecked(event, row, i)
                            }
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ padding: "8px" }}>
                          <Checkbox
                            type="checkbox"
                            sx={{
                              "& .MuiSvgIcon-root": {
                                fontSize: 25,
                                color: "#3299FF",
                              },
                            }}
                            checked={row.AttendedTechTestRecovery}
                            name="AttendedTechTestRecovery"
                            onChange={(event) =>
                              handleChangeChecked(event, row, i)
                            }
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ padding: "8px" }}>
                          <Checkbox
                            type="checkbox"
                            sx={{
                              "& .MuiSvgIcon-root": {
                                fontSize: 25,
                                color: "#3299FF",
                              },
                            }}
                            checked={row.AttendedTTF}
                            name="AttendedTTF"
                            onChange={(event) =>
                              handleChangeChecked(event, row, i)
                            }
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ padding: "8px" }}>
                          <Checkbox
                            type="checkbox"
                            sx={{
                              "& .MuiSvgIcon-root": {
                                fontSize: 25,
                                color: "#3299FF",
                              },
                            }}
                            checked={row.AttendedProductTraining}
                            name="AttendedProductTraining"
                            onChange={(event) =>
                              handleChangeChecked(event, row, i)
                            }
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ padding: "8px" }}>
                          <Checkbox
                            type="checkbox"
                            checked={row.AttendedNesting}
                            name="AttendedNesting"
                            sx={{
                              "& .MuiSvgIcon-root": {
                                fontSize: 25,
                                color: "#3299FF",
                              },
                            }}
                            onChange={(event) =>
                              handleChangeChecked(event, row, i)
                            }
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ padding: "8px" }}>
                          <Checkbox
                            type="checkbox"
                            sx={{
                              "& .MuiSvgIcon-root": {
                                fontSize: 25,
                                color: "#3299FF",
                              },
                            }}
                            checked={row.AttendedProduction}
                            name="AttendedProduction"
                            onChange={(event) =>
                              handleChangeChecked(event, row, i)
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          <SnackBar snack={snack} snackReset={snackReset}></SnackBar>

          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              paddingTop: 1,
              gap: 1,
            }}
          >
            <CustomOutlinedButton variant="outlined" onClick={navigateBack}>
              {t("CANCEL")}
            </CustomOutlinedButton>
            <CustomButton onClick={(e) => saveAttendance()}>
              {t("SAVE")}
            </CustomButton>
            <CustomButton onClick={(e) => saveCloseHandler()}>
              {t("SAVE+CLOSE")}
            </CustomButton>
          </Grid>
        </>
      )}
    </div>
  );
};

AttendanceV2.propTypes = {
  filterStatus: PropTypes.string,
  roster: PropTypes.string,
  closeLoader: PropTypes.func,
  resetStatus: PropTypes.bool,
  onChange: PropTypes.func,
};

export default AttendanceV2;
